"use client";

import { useState } from "react";
import { Notice, SelectField } from "@/components/tool-kit/primitives";
import {
  alphaOptions,
  ExampleButton,
} from "@/components/tools/stats-tests/kit";
import { CochransQResults } from "@/components/tools/stats-tests-more/cochrans-q-results";
import {
  MatrixInput,
  matrixProblemText,
} from "@/components/tools/stats-tests-more/matrix-input";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  cochransQ,
  firstNonBinary,
} from "@/lib/tool-kit/stats-tests-more/cochran-q";
import {
  conditionNames,
  parseMatrix,
} from "@/lib/tool-kit/stats-tests-more/matrix";

/** English strings. A translated content file overrides these through `ui`. */
export const COCHRANS_Q_TEST_CALCULATOR_STRINGS = {
  dataLabel:
    "Outcomes — one row per subject, one column per condition, coded 1 (yes / success) or 0 (no / failure)",
  dataPlaceholder: "1, 1, 1\n0, 1, 1\n0, 0, 1",
  namesLabel: "Condition names (optional, separated by commas)",
  namesPlaceholder: "Multiple choice, Short answer, Essay",
  conditionDefault: "Condition {n}",
  subjectsDetected: "one: subject|other: subjects",
  conditionsDetected: "one: condition|other: conditions",
  errorInvalid: "Line {line}: “{token}” is not a number.",
  errorRagged:
    "Line {line} has {got} values, but the first row has {expected}. Every subject needs an outcome in every condition.",
  errorNotBinary:
    "Row {row}, column {column} is {value}. Every outcome must be coded 1 (yes / success) or 0 (no / failure).",
  alphaLabel: "Significance level (α)",
  tryExample: "Try an example",
  exampleNames: "Multiple choice, Short answer, Essay",
  exampleData:
    "1, 1, 1\n0, 1, 1\n0, 0, 1\n1, 1, 1\n0, 0, 0\n0, 1, 1\n0, 0, 1\n1, 0, 1\n0, 1, 1\n0, 0, 1\n1, 1, 1\n0, 1, 0\n0, 0, 1\n0, 1, 1\n1, 1, 1",
  results: "Results",
  qStatistic: "Cochran's Q",
  df: "Degrees of freedom",
  pValue: "p-value",
  kendallW: "Kendall's W (Q / n(k − 1))",
  subjects: "Subjects (n)",
  informative: "Informative subjects",
  decisionReject:
    "Reject H₀ at α = {alpha}: p = {p} is below α — the proportion of successes is not the same in every condition.",
  decisionKeep:
    "Do not reject H₀ at α = {alpha}: p = {p} is not below α — no evidence that the conditions differ.",
  smallSample:
    "The χ² approximation is unreliable here: Cochran's guideline asks for at least 4 informative subjects and n × k of at least 24.",
  informativeNote:
    "Subjects with the same outcome in every condition (all 0 or all 1) say nothing about differences between conditions and do not affect Q.",
  summaryHeading: "Successes by condition",
  condition: "Condition",
  successes: "Successes (1s)",
  total: "Subjects",
  proportion: "Proportion",
  posthocHeading: "Pairwise McNemar tests",
  posthocHint:
    "Each pair is compared with McNemar's test on the discordant subjects — b, who succeeded in the first condition only, and c, in the second only. The exact p-value is adjusted for the {m} comparisons.",
  comparison: "Comparison",
  b: "b (first only)",
  c: "c (second only)",
  pExact: "p (exact)",
  pChi: "p (χ², corrected)",
  pBonferroni: "p (Bonferroni)",
  pHolm: "p (Holm)",
  versus: "{a} vs {b}",
  matrixHeading: "Outcomes with row totals",
  matrixHint: "Row totals Rᵢ and column totals Cⱼ are the inputs to Q.",
  matrixHidden: "The outcome table is shown for up to 40 subjects.",
  subject: "Subject",
  rowTotal: "Total (Rᵢ)",
  columnTotals: "Column total (Cⱼ)",
  howCalculated: "How this was calculated",
  workingTotals: "T = ΣCⱼ = {t}; ΣCⱼ² = {sc}; ΣRᵢ² = {sr}; k = {k}.",
  workingQ:
    "Q = (k − 1)[k ΣCⱼ² − T²] / (k T − ΣRᵢ²) = {kk} × [{k} × {sc} − {t}²] / ({k} × {t} − {sr}) = {q}",
  workingP: "df = k − 1 = {df}; p = P(χ²({df}) ≥ {q}) = {p}.",
  apaHeading: "APA-style result",
  apaTemplate: "Cochran's *Q*({df}, *N* = {n}) = {q}, *p* {p}",
  apaNote:
    "Report the proportion of successes in each condition, then the pairwise McNemar tests that locate the difference, noting the adjustment used.",
  copyApa: "Copy APA result",
  copied: "Copied!",
  prompt:
    "Enter at least two subjects and at least two conditions, coded 1 and 0.",
  errorNoVariation:
    "Every subject has the same outcome in every condition, so there is nothing to compare and Q is undefined.",
} as const;

export default function CochransQTestCalculatorWidget() {
  const strings = useToolUi(COCHRANS_Q_TEST_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [data, setData] = useState("");
  const [names, setNames] = useState("");
  const [alpha, setAlpha] = useState("0.05");

  const parsed = parseMatrix(data);
  let notice: string | null = matrixProblemText(parsed, strings, fmt);
  const bad = notice === null ? firstNonBinary(parsed.rows) : null;
  if (bad) {
    notice = t(strings.errorNotBinary, {
      row: fmt(bad.row),
      column: fmt(bad.column),
      value: fmt(bad.value),
    });
  }
  const outcome =
    notice === null && parsed.rows.length >= 2 ? cochransQ(parsed.rows) : null;
  const labels = conditionNames(names, parsed.header, parsed.width, (n) =>
    t(strings.conditionDefault, { n })
  );
  if (notice === null && outcome && !outcome.ok) {
    notice =
      outcome.reason === "no-variation"
        ? strings.errorNoVariation
        : strings.prompt;
  } else if (notice === null && !outcome) {
    notice = strings.prompt;
  }

  return (
    <div className="space-y-6">
      <MatrixInput
        data={data}
        fmt={fmt}
        idPrefix="cq"
        locale={locale}
        names={names}
        onData={setData}
        onNames={setNames}
        parsed={parsed}
        text={strings}
      />
      <div className="flex flex-wrap items-end gap-4">
        <div className="w-full max-w-xs">
          <SelectField
            id="cq-alpha"
            label={strings.alphaLabel}
            onChange={setAlpha}
            options={alphaOptions(fmt)}
            value={alpha}
          />
        </div>
        <ExampleButton
          label={strings.tryExample}
          onClick={() => {
            setData(strings.exampleData);
            setNames(strings.exampleNames);
          }}
        />
      </div>
      {outcome?.ok ? (
        <CochransQResults
          alpha={Number(alpha)}
          fmt={fmt}
          names={labels}
          r={outcome.result}
          rows={parsed.rows}
          strings={strings}
        />
      ) : (
        <Notice>{notice}</Notice>
      )}
    </div>
  );
}
